import {useEffect, useState} from "react";
import {getPrompts, getPromptsAvailable} from "../services/PromptsService";

/**
 * Custom hook to manage the prompt form state and logic
 * @param {Object} company The company object
 * @param {boolean} isOpen Whether the dialog is open
 * @returns {Object} Form state and handlers
 */
export const usePromptForm = (company, isOpen) => {
  // Initial form state
  const initialFormState = {
    name: "",
    category: "ARTICLES",
    type: "PROMPT",
    createOption: "CREATE_NEW",
    format: "",
    systemPrompt: "",
    existingFormat: ""
  };

  // Data states
  const [formData, setFormData] = useState(initialFormState);
  const [formatData, setFormatData] = useState({
    formats: [],
    existingFormats: [],
    subFormats: []
  });
  const [systemPrompts, setSystemPrompts] = useState([]);

  // UI states
  const [isLoading, setIsLoading] = useState(false);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setFormData(initialFormState);
    }
  }, [isOpen]);

  // Data fetching based on form state changes
  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);

      try {
        // Skip format fetching if we're not in override mode and don't have a company
        if (!company && formData.createOption === "OVERRIDE") {
          setIsLoading(false);
          return;
        }
        // Case 1: Fetch formats for SUB_FORMAT creation - works with or without company
        if (formData.type === "SUB_FORMAT" && formData.createOption === "CREATE_NEW") {
          const res = await getPrompts({
                companyId: company?.id,
              category: formData.category,
              type: 'FORMAT'
              });
          if (!isMounted) return;

          setFormatData(prev => ({ ...prev, formats: res.data }));

          if (res.data.length > 0) {
            setFormData(prev => ({ ...prev, format: res.data[0].id }));
          }
        }
        // Case 2, 3, 4: Use getAvailableEntities for PROMPT, FORMAT and SUB_FORMAT
        else if (company) {
          let entityType;

          if (formData.type === "PROMPT") {
            entityType = "PROMPT";
          } else if (formData.createOption === "OVERRIDE") {
            if (formData.type === "FORMAT") {
              entityType = "FORMAT";
            } else if (formData.type === "SUB_FORMAT") {
              entityType = "SUB_FORMAT";
            }
          }

          if (entityType) {
            const res = await getPromptsAvailable(company.id, formData.category, entityType);
            if (!isMounted) return;

            if (entityType === "PROMPT") {
              setSystemPrompts(res.data);

              if (res.data.length > 0) {
                const selectedItem = res.data[0];
                setFormData(prev => ({
                  ...prev,
                  systemPrompt: selectedItem.id,
                  name: selectedItem.name
                }));
              }
            } else if (entityType === "FORMAT") {
              setFormatData(prev => ({ ...prev, existingFormats: res.data }));

              if (res.data.length > 0) {
                setFormData(prev => ({
                  ...prev,
                  existingFormat: res.data[0].id,
                  name: res.data[0].name
                }));
              }
            } else if (entityType === "SUB_FORMAT") {
              setFormatData(prev => ({ ...prev, subFormats: res.data }));

              if (res.data.length > 0) {
                setFormData(prev => ({
                  ...prev,
                  existingFormat: res.data[0].id,
                  name: res.data[0].name
                }));
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [company, formData.category, formData.type, formData.createOption, isOpen]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for different fields
    if (name === "systemPrompt" && company) {
      const selectedPrompt = systemPrompts.find(prompt => prompt.id === value);
      if (selectedPrompt) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          name: selectedPrompt.name
        }));
        return;
      }
    }

    if (name === "existingFormat" && company) {
      let selectedItem;

      if (formData.type === "FORMAT") {
        selectedItem = formatData.existingFormats.find(format => format.id === value);
      } else if (formData.type === "SUB_FORMAT") {
        selectedItem = formatData.subFormats.find(subFormat => subFormat.id === value);
      }

      if (selectedItem) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          name: selectedItem.name
        }));
        return;
      }
    }

    // Handle type changes
    if (name === "type") {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        createOption: "CREATE_NEW",
        existingFormat: "",
        format: ""
      }));
      return;
    }

    // Default handling
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Validate form data
  const isFormValid = () => {
    if (formData.name === "") return false;

    if (formData.type === "SUB_FORMAT" && formData.createOption === "CREATE_NEW" && !formData.format) {
      return false;
    }

    if (formData.type === "PROMPT" && company && formData.systemPrompt === "") {
      return false;
    }

    if (company && (formData.type === "FORMAT" || formData.type === "SUB_FORMAT")) {
      if (formData.createOption === "OVERRIDE" && !formData.existingFormat) {
        return false;
      }
    }

    return true;
  };

  // Prepare data for submission
  const getSubmitData = () => {
    const submitData = {
      name: formData.name,
      companyId: company?.id,
      category: formData.category,
      type: formData.type,
      formatId: formData.type === "SUB_FORMAT" && formData.createOption === "CREATE_NEW" ? formData.format : undefined,
      systemPromptId: formData.type === "PROMPT" ? formData.systemPrompt : (formData.createOption === "OVERRIDE" && company) ? formData.existingFormat : undefined,
    };

    // If overriding a subformat, include the parent format ID
    if (formData.type === "SUB_FORMAT" && formData.createOption === "OVERRIDE" && company) {
      // Find the selected subformat to get its parent format ID
      const selectedSubFormat = formatData.subFormats.find(
          subFormat => subFormat.id === formData.existingFormat
      );

      if (selectedSubFormat && selectedSubFormat.formatId) {
        // Add the format ID of the selected subformat
        submitData.formatId = selectedSubFormat.formatId;
      }
    }

    return submitData;
  };

  // Reset form
  const resetForm = () => {
    setFormData(initialFormState);
  };

  return {
    formData,
    formatData,
    systemPrompts,
    isLoading,
    handleChange,
    isFormValid,
    getSubmitData,
    resetForm
  };
};
